import { Component, EventEmitter, Input, Output, SimpleChanges, OnChanges, ViewChild } from '@angular/core';
import { FileUpload } from 'primeng/fileupload';
import { Dialog } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { UploadedFile } from 'src/app/models/fileUpload';

@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
  standalone: true,
  imports: [FileUpload, Dialog, ButtonModule],
  providers: [ButtonModule ]
})
export class FileUploadComponent implements OnChanges {
  @Input() addFileLabel: string = 'Add File';
  @Input() addFileIcon: string = '';
  @Input() accpetType: string = '*';
  @Input() initialFiles: UploadedFile[] = []; 
  @Output() fileChanged = new EventEmitter<UploadedFile[]>();

  @ViewChild('uploader') fileUploadComponent!: FileUpload;

  uploadedFiles: UploadedFile[] = [];
  showFileDeleteDialog = false;
  fileToDelete: UploadedFile | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialFiles']) {
      this.uploadedFiles = [...(this.initialFiles ?? [])];
      this.fileChanged.emit(this.uploadedFiles);
    }
  }

  onFileSelected(event: any) {
    const files: File[] = Array.from(event.files || []);
    this.addFiles(files);
  }

  onPaste(ev: ClipboardEvent) {
    const items = ev.clipboardData?.items;
    if (!items?.length) return;

    const files: File[] = [];
    for (const item of Array.from(items)) {
      if (item.kind === 'file') {
        const f = item.getAsFile();
        if (f) files.push(f); 
      }
    }

    if (files.length) {
      ev.preventDefault();
      ev.stopPropagation();
      this.addFiles(files);
    }
  }

  onDragOver(ev: DragEvent) {
    ev.preventDefault();     
  }

  onDrop(ev: DragEvent) {
    ev.preventDefault();
    const files = Array.from(ev.dataTransfer?.files ?? []);
    if (files.length) this.addFiles(files);
  }

  private addFiles(files: File[]) {
    const accepted: File[] = files.filter(f => this.isAllowed(f));
    for (const file of accepted) {
      this.uploadedFiles.push({ file, name: file.name, isSaved: false });

      // keep PrimeNG's internal list in sync (optional)
      if (this.fileUploadComponent?.files) {
        this.fileUploadComponent.files.push(file);
      }
    }
    if (accepted.length) this.fileChanged.emit(this.uploadedFiles);
  }

  private isAllowed(file: File): boolean {
    const accept = (this.accpetType || '*').trim();
    if (accept === '*' || accept === '') return true;

    const patterns = accept.split(',').map(s => s.trim().toLowerCase());
    const name = file.name.toLowerCase();
    const type = (file.type || '').toLowerCase();

    return patterns.some(p => {
      if (p.startsWith('.')) {
        // extension, e.g. .png
        return name.endsWith(p);
      }
      if (p.endsWith('/*')) {
        // wildcard mime, e.g. image/*
        const base = p.slice(0, -2);
        return type.startsWith(base + '/');
      }
      // exact mime, e.g. image/png or application/pdf
      return type === p;
    });
  }

  confirmRemove(file: UploadedFile) {
    this.fileToDelete = file;
    this.showFileDeleteDialog = true;
  }

  cancelFileDeletion() {
    this.showFileDeleteDialog = false;
    this.fileToDelete = null;
  }

  confirmFileDeletion() {
    if (!this.fileToDelete) return;

    const file = this.fileToDelete;
    this.uploadedFiles = this.uploadedFiles.filter(f => f !== file);
    this.fileChanged.emit(this.uploadedFiles);
    this.removeFromPrimeNgList(file);

    this.showFileDeleteDialog = false;
    this.fileToDelete = null;
  }


  private removeFromPrimeNgList(file: UploadedFile) {
    if (this.fileUploadComponent?.files) {
      const idx = this.fileUploadComponent.files.findIndex(f => f.name === file.name);
      if (idx > -1) this.fileUploadComponent.files.splice(idx, 1);
    }
  }

  // Allow parent to reset the UI
  reset(): void {
    this.uploadedFiles = [];
    this.fileChanged.emit([]);
    if (this.fileUploadComponent) this.fileUploadComponent.clear();
  }
}

